import React, { FC, memo, useCallback } from 'react'
import { Slide } from 'pure-react-carousel'

import ListingSectionTransport from '../../../transports/common/listing-section-transport'
import GridCard from '../../common/grid-card'
import {
    CtaButtonText,
    CtaContainer,
    Section,
    StyledGhostButton,
    Subtitle,
    Title,
    TitleContainer,
} from '../common/styles'
import { useTranslation } from '../../../hooks/locale'
import { BreadcrumbListTransport } from '../../../models/common/breadcrumb-model'
import { Offer } from '../../../models/common/offers-model'
import { SuitablesModel } from '../../../models/common/suitables-model'
import PrevNextControls from '../../common/prev-next-controls'
import { useResponsiveCards } from '../../../hooks/responsive'
import { SliderContainer, StyledCarouselProvider, StyledSlider } from '../../common/listing-card-section/styles'
import { cardEventCategories, pageTypeEventCategories } from '../../../services/analytics-service/categories'

interface Props {
    listingSection: ListingSectionTransport
    url: string
    sitePathname?: string
    defaultHeading: string
    defaultSubheading?: string
    breadcrumbModel: BreadcrumbListTransport
    offerModel: Offer
    suitablesModel: SuitablesModel
}

const TopOffersSection: FC<Props> = ({
    listingSection,
    url,
    sitePathname,
    defaultHeading,
    defaultSubheading,
    breadcrumbModel,
    offerModel,
    suitablesModel,
}) => {
    const { t } = useTranslation()

    const { objects, cta } = listingSection

    const heading = listingSection.heading || defaultHeading
    const subheading = listingSection.subheading || defaultSubheading

    const visibleSlides = useResponsiveCards()
    const dragStep = Math.floor(visibleSlides)

    const _handleCTAButtonClick = useCallback(() => {
        document.location.href = cta?.url || url
    }, [url, cta?.url])

    return (
        <Section id="top-offers">
            <TitleContainer>
                <Title>{heading}</Title>
                {!!subheading && <Subtitle>{subheading}</Subtitle>}
            </TitleContainer>
            <SliderContainer>
                <StyledCarouselProvider
                    totalSlides={objects.length}
                    visibleSlides={visibleSlides}
                    naturalSlideWidth={1}
                    naturalSlideHeight={1}
                    isIntrinsicHeight
                    lockOnWindowScroll
                    dragStep={dragStep}
                    step={dragStep}
                >
                    <PrevNextControls totalObjects={objects.length} visibleSlides={visibleSlides} />
                    <StyledSlider $transitionTime={0.2}>
                        {objects.map((object, index) => (
                            <Slide index={index} key={object.id}>
                                <GridCard
                                    key={object.id}
                                    index={index}
                                    listingObject={object}
                                    requestUrl={url}
                                    sitePathname={sitePathname}
                                    pageName={pageTypeEventCategories.HOME}
                                    sectionName={cardEventCategories.sections.TOP_OFFERS}
                                    breadcrumbModel={breadcrumbModel}
                                    offerModel={offerModel}
                                    suitablesModel={suitablesModel}
                                    showFeatures={false}
                                />
                            </Slide>
                        ))}
                    </StyledSlider>
                </StyledCarouselProvider>
            </SliderContainer>
            {!!cta?.url && (
                <CtaContainer>
                    <StyledGhostButton onClick={_handleCTAButtonClick} borderRadius="1rem">
                        <CtaButtonText>{t('seeAll')}</CtaButtonText>
                    </StyledGhostButton>
                </CtaContainer>
            )}
        </Section>
    )
}

export default memo(TopOffersSection)
